import { useMemo, useEffect, useState } from 'react';
import { deepmerge } from '@mui/utils';
import {
    Box,
    Card,
    CardProps,
    CardContent,
    CardContentProps,
    CardHeader,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { HelpOutlineOutlined as HelpOutlineOutlinedIcon } from '@mui/icons-material';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import Column from '../Column';
import Printable from '../Printable';
import Row from '../Row';
import SkeletonPieChart from './SkeletonPieChart';
import theme from '../../theme';
import Utils from '../../components/Utils';

export const pieChartSliceColors = [
    '#0e59e7',
    '#6771dc',
    '#8067dc',
    '#a367dc',
    '#c767dc',
    '#dc67ab',
    '#dc6788',
    '#dc6967',
    '#dc8c67',
    '#dcaf67',
    '#dcd267',
    '#c3dc67',
    '#a0dc67',
    '#7ddc67',
    '#67dc75',
    '#67dc98',
    '#67dcbb',
    '#67dadc',
];

export default function PieChartCard(props: {
    label?: string | null | undefined | React.ReactElement;
    tooltip?: string;
    chartOptions?: ApexOptions;
    chartSeries?: ApexAxisChartSeries | number[];
    noDataOverlay?: React.ReactElement;
    loading?: boolean;
    slotProps?: {
        card?: CardProps;
        cardContent?: CardContentProps;
    };
}) {
    const { label, tooltip, chartOptions, chartSeries, noDataOverlay, loading = false, slotProps = {} } = props;

    const [chartWidth, setChartWidth] = useState<number>(700);

    // Responsive adjustments
    const isSmallScreen = useMediaQuery('(max-width:600px)');
    const isMediumScreen = useMediaQuery('(max-width:1000px)');

    useEffect(() => {
        if (isSmallScreen) {
            setChartWidth(350);
        } else if (isMediumScreen) {
            setChartWidth(550);
        } else {
            setChartWidth(700);
        }
    }, [isSmallScreen, isMediumScreen]);

    const defaultChartOptions: ApexOptions = {
        colors: pieChartSliceColors,
        chart: {
            type: 'donut',
            width: '100%',
        },
        legend: {
            position: isSmallScreen ? 'bottom' : 'right',
            floating: false,
            offsetY: 0,
            width: 300,
            fontSize: '12px',
            fontFamily: theme.typography.fontFamily,
            fontWeight: 400,
            horizontalAlign: 'left',
            markers: {
                size: 7,
                shape: 'square',
                offsetX: -4,
            },
            itemMargin: {
                horizontal: 4,
                vertical: 4,
            },
        },
        plotOptions: {
            pie: {
                donut: {
                    labels: {
                        show: true,
                        name: {
                            show: true,
                            offsetY: 20,
                        },
                        value: {
                            show: true,
                            color: theme.palette.text.primary,
                            fontFamily: theme.typography.fontFamily,
                            fontSize: '20px',
                            offsetY: -20,
                            formatter: function (value) {
                                return `${value}%`;
                            },
                        },
                        total: {
                            show: true,
                            showAlways: true,
                            label: 'Total',
                            color: theme.palette.text.secondary,
                            fontFamily: theme.typography.fontFamily,
                            fontSize: '12px',
                            formatter: function (value) {
                                return chartSeries && chartSeries.length > 0 ? `${chartSeries[0]}%` : '';
                            },
                        },
                    },
                },
            },
        },
        tooltip: {
            enabled: true,
            y: {
                formatter: (value) => `${Utils.formatNumber(value)}%`,
            },
        },
        dataLabels: {
            enabled: true,
            dropShadow: { enabled: false },
            formatter: (value: any) => `${Utils.formatNumber(value)}%`,
        },
        responsive: [
            {
                breakpoint: 600,
                options: {
                    chart: {
                        width: 250,
                    },
                    legend: {
                        position: 'bottom',
                        floating: false,
                    },
                },
            },
            {
                breakpoint: 1000,
                options: {
                    chart: {
                        width: 350,
                    },
                    legend: {
                        position: 'bottom',
                    },
                },
            },
        ],
    };

    const hasData = useMemo(() => chartSeries && chartSeries.length > 0, [chartSeries]);
    const hasNoDataOverlay = useMemo(() => noDataOverlay !== null && noDataOverlay !== undefined, [noDataOverlay]);

    return (
        <Card variant="outlined" sx={{ overflow: 'visible' }} {...slotProps?.card}>
            {label && (
                <CardHeader
                    title={
                        <Row gap={0.5} alignItems="center">
                            <Typography variant="body1" color="text.primary">
                                {label}
                            </Typography>

                            <Printable show={false}>
                                {tooltip && (
                                    <Tooltip title={tooltip}>
                                        <HelpOutlineOutlinedIcon color="action" sx={{ fontSize: 16 }} />
                                    </Tooltip>
                                )}
                            </Printable>
                        </Row>
                    }
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                />
            )}
            <CardContent
                sx={{
                    display: 'flex',
                    flexDirection: isSmallScreen ? 'column' : 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    overflow: 'hidden',
                    paddingBottom: 2,
                    height: '100%',
                }}
                {...slotProps?.cardContent}
            >
                {loading ? (
                    <SkeletonPieChart />
                ) : hasData ? (
                    <Box>
                        <ReactApexChart
                            type="donut"
                            options={deepmerge(defaultChartOptions, chartOptions)}
                            series={chartSeries}
                            height={isMediumScreen || isSmallScreen ? 600 : 285}
                            width={chartWidth}
                        />
                    </Box>
                ) : (
                    <>
                        {hasNoDataOverlay ? (
                            noDataOverlay
                        ) : (
                            <Column direction="column" alignItems="center" justifyContent="center" sx={{ height: 300 }}>
                                <Typography variant="body2">No data available</Typography>
                            </Column>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}
